import * as tagActions from 'spa/action_creators/tags'
import { Badge } from '../../components/badge/badge'
import {
  defineMessages,
  FormattedHTMLMessage,
  FormattedMessage,
  injectIntl,
} from 'react-intl'
import { DropdownPopover } from 'spa/components/filters/dropdown_popover'
import { Heading } from 'spa/components/typography'
import { isMobileSelector } from 'spa/components/../selectors/device'
import { Knotch } from 'spa/components/knotch'
import { Link } from 'spa/components/link'
import { orderByOptions } from 'spa/shared/utils'
import { SearchBox } from 'spa/components/search_box'
import { seoMessages } from 'spa/components/app_wrapper/localizedMessages'
import { SORT_OPTIONS } from 'spa/components/filters'
import { TextStyle } from 'spa/components/typography'
import { TogglePanel } from 'spa/components/toggle_panel'
import { useDispatch, useSelector } from 'react-redux'
import orderBy from 'lodash/orderBy'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'

import styles from './styles'

const localizeMessages = defineMessages({
  placeholder: {
    id: 'djcity.common.genre.search.placeholder',
    defaultMessage: 'Search genre...',
  },
  popularity: {
    id: 'djcity.common.genre.search.popularity',
    defaultMessage: 'Popularity',
  },
  asc: {
    id: 'djcity.common.genre.search.asc',
    defaultMessage: 'A-Z',
  },
  actionLink1: {
    id: 'djcity.common.genre.action.1.link',
    defaultMessage: 'hip-hop',
  },
  actionLink2: {
    id: 'djcity.common.genre.action.2.link',
    defaultMessage: 'djcity-exclusive',
  },
  actionLink3: {
    id: 'djcity.common.genre.action.3.link',
    defaultMessage: 'latin',
  },
  actionLink4: {
    id: 'djcity.common.genre.action.4.link',
    defaultMessage: 'african',
  },
  actionLink5: {
    id: 'djcity.common.genre.action.5.link',
    defaultMessage: 'tech-house',
  },
})

const TagSearch = ({ intl }) => {
  const dispatch = useDispatch()

  const { tags, isLoading: isLoadingTags } = useSelector(s => s.tags)
  const isMobile = useSelector(isMobileSelector)

  const [state, setState] = useState({
    searchTerm: '',
    sortBy: 5,
  })

  useEffect(() => {
    if (!tags && !isLoadingTags) {
      dispatch(tagActions.fetchTags())
    }
  }, [])

  const handleChangeSorting = sortBy => setState({ ...state, sortBy })

  const handleSearchChange = e =>
    setState({ ...state, searchTerm: e.target.value })

  const handleClear = () => setState({ ...state, searchTerm: '' })

  const { searchTerm, sortBy } = state
  let filteredTags = tags
    ? tags.filter(
        tag =>
          tag.name &&
          tag.name.toLowerCase().indexOf(searchTerm?.toLowerCase()) >= 0
      )
    : []
  if (filteredTags.length && sortBy !== SORT_OPTIONS.POPULARITY) {
    const { mode: sortMode } = orderByOptions[sortBy - 1]
    filteredTags = orderBy(filteredTags, 'name', [sortMode])
  }

  return (
    <div className={styles.base}>
      <div className={styles.header}>
        <TextStyle uppercase variant="extra-bold">
          <FormattedMessage
            defaultMessage="GENRES"
            id="djcity.common.genre.search.uptitle"
          />
        </TextStyle>
        <div className={styles.headingWrapper}>
          <Heading headingVariant="disguisedH2">
            <FormattedMessage
              defaultMessage="ALL GENRES"
              id="djcity.common.genre.search.title"
            />
          </Heading>
          {!isMobile && (
            <div>
              <TogglePanel
                items={[
                  {
                    label: intl.formatMessage(localizeMessages.popularity),
                    value: SORT_OPTIONS.POPULARITY,
                  },
                  {
                    label: intl.formatMessage(localizeMessages.asc),
                    value: SORT_OPTIONS.TITLE_ASC,
                  },
                ]}
                onChange={handleChangeSorting}
                value={sortBy}
              />
            </div>
          )}
        </div>
        <Heading headingVariant="disguisedH1">
          {intl.formatMessage(seoMessages.genresH1)}
        </Heading>
        <Knotch className={styles.knotch} size="big" />
      </div>
      <div className={styles.actionButtonsWrapper}>
        <div className={styles.actionButtons}>
          <Link
            to={`/genre/${intl.formatMessage(localizeMessages.actionLink1)}`}
          >
            <FormattedHTMLMessage
              defaultMessage="Hip Hop"
              id="djcity.common.genre.action.1.title"
            />
          </Link>
          <Link
            to={`/genre/${intl.formatMessage(localizeMessages.actionLink2)}`}
          >
            <FormattedHTMLMessage
              defaultMessage="Djcity Exclusive"
              id="djcity.common.genre.action.2.title"
            />
          </Link>
          <Link
            to={`/genre/${intl.formatMessage(localizeMessages.actionLink3)}`}
          >
            <FormattedHTMLMessage
              defaultMessage="Latin"
              id="djcity.common.genre.action.3.title"
            />
          </Link>
          <Link
            to={`/genre/${intl.formatMessage(localizeMessages.actionLink4)}`}
          >
            <FormattedHTMLMessage
              defaultMessage="African"
              id="djcity.common.genre.action.4.title"
            />
          </Link>
          <Link
            to={`/genre/${intl.formatMessage(localizeMessages.actionLink5)}`}
          >
            <FormattedHTMLMessage
              defaultMessage="Tech House"
              id="djcity.common.genre.action.5.title"
            />
          </Link>
        </div>
      </div>
      <SearchBox
        className={styles.tagSearch}
        onChange={handleSearchChange}
        placeholder={intl.formatMessage(localizeMessages.placeholder)}
        reset={isMobile ? handleClear : () => {}}
        value={searchTerm}
      />
      {isMobile && (
        <div className={styles.filters}>
          <DropdownPopover
            isMobile={isMobile}
            items={{
              [SORT_OPTIONS.POPULARITY]: intl.formatMessage(
                localizeMessages.popularity
              ),
              [SORT_OPTIONS.TITLE_ASC]: intl.formatMessage(
                localizeMessages.asc
              ),
            }}
            onChange={handleChangeSorting}
            value={sortBy}
          />
        </div>
      )}
      {!isMobile && searchTerm.length > 0 && (
        <button
          className={'secondary ' + styles.tagSearchButton}
          onClick={handleClear}
        >
          <FormattedMessage
            defaultMessage="Clear results"
            id="djcity.records.tag.button.clear"
          />
        </button>
      )}
      <div className={styles.container}>
        {filteredTags.map(item => {
          return (
            <Link key={item.id} to={`/genre/${item.slug}`}>
              <div className={styles.cell}>
                <TextStyle className={styles.tagText}>{item.name}</TextStyle>
                <Badge createdOn={item.createdOn} updatedOn={item.updatedOn} />
              </div>
            </Link>
          )
        })}
      </div>
    </div>
  )
}

TagSearch.propTypes = {
  intl: PropTypes.shape({
    formatDate: PropTypes.func,
    formatMessage: PropTypes.func,
  }).isRequired,
}

export default injectIntl(TagSearch)
